import { useState, useMemo, useEffect } from "react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faFilter,
  faTimes,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import AddCapital from "./AddCapital";
import EditCapital from "./EditCapital";
import SearchBar from "../../../components/ui/SearchBar";
import TableWrapper from "../../../components/ui/TableWrapper";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import { useAuth, useCapitals } from "../../../hooks";

const CapitalTab = ({ shopId }) => {
  const { hasPermission } = useAuth();
  const {
    capitals,
    pagination,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refresh,
    createCapital,
    updateCapital,
  } = useCapitals(shopId, { per_page: 1000 });

  const canManageCapital = hasPermission([
    "manage_capital",
    "manage_shop_modules",
  ]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCapital, setSelectedCapital] = useState(null);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // Advanced Filter Modal State
  const [isAdvFilterOpen, setIsAdvFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    startDate: "",
    endDate: "",
  });

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounce search to prevent UI reload on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(localSearchTerm);
      setCurrentPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearchTerm, setSearchTerm, setCurrentPage]);

  const safeCapitals = useMemo(
    () => (Array.isArray(capitals) ? capitals : []),
    [capitals],
  );

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("sw-TZ", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount || 0)) + " TSH";

  const filteredCapitals = useMemo(() => {
    let result = safeCapitals;

    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((cap) => {
        const d = new Date(cap.date);
        return d >= start && d <= end;
      });
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(
        (cap) =>
          cap.capitalId?.toLowerCase().includes(search) ||
          String(cap.amount).includes(search) ||
          (cap.date || "").includes(search),
      );
    }

    return [...result].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [safeCapitals, searchTerm, startDate, endDate, isFilterApplied]);

  const totalItems = pagination?.total ?? filteredCapitals.length;
  const totalPages = pagination?.last_page ?? 1;
  const startIndex = (currentPage - 1) * pageSize;

  const totalCapital = safeCapitals.reduce(
    (sum, cap) => sum + Number(cap.amount || 0),
    0,
  );

  const handleAddCapital = async (data) => {
    await createCapital({ ...data, shopId });
    setIsAddModalOpen(false);
  };

  const handleEditCapital = async (data) => {
    const id = data?.capitalId || data?.id;
    if (id) {
      await updateCapital(id, data);
    }
    setIsEditModalOpen(false);
    setSelectedCapital(null);
  };

  // --- Advanced Filter Handlers ---
  const openAdvFilter = () => {
    setTempFilters({
      startDate: startDate || "",
      endDate: endDate || "",
    });
    setIsAdvFilterOpen(true);
  };

  const applyAdvFilters = () => {
    setStartDate(tempFilters.startDate);
    setEndDate(tempFilters.endDate);
    setIsFilterApplied(!!(tempFilters.startDate || tempFilters.endDate));
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const clearAdvFilters = () => {
    setTempFilters({
      startDate: "",
      endDate: "",
    });
    setStartDate("");
    setEndDate("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  if (loading && safeCapitals.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-sm text-gray-600">
        Loading capital records...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center gap-3">
        <p className="text-sm text-red-600">Failed to load capital records.</p>
        <Button
          onClick={() => refresh()}
          className="bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">
            Capital management
          </h3>
          <p className="text-sm text-gray-600">Track and manage shop capital</p>
        </div>
        <div className="flex items-center gap-3">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 px-4 py-2">
            <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold">
              Total Capital
            </p>
            <p className="text-lg font-bold text-blue-700">
              {formatCurrency(totalCapital)}
            </p>
          </Card>
          {canManageCapital && (
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add capital
            </Button>
          )}
        </div>
      </div>

      {/* Main Toolbar: Search Left, Advanced Filter Right */}
      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={localSearchTerm}
          onChange={(value) => setLocalSearchTerm(value)}
          placeholder="Search by ID, amount, or date..."
          className="flex-1"
        />
        <Button
          onClick={openAdvFilter}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto px-6"
        >
          <FontAwesomeIcon icon={faFilter} />
          Advanced Filter
          {isFilterApplied && (
            <span className="ml-1 w-2 h-2 rounded-full bg-orange-400"></span>
          )}
        </Button>
      </div>

      <TableWrapper
        title="capital records"
        totalItems={totalItems}
        currentPage={currentPage}
        totalPages={totalPages}
        className="bg-white border border-gray-200 shadow-sm"
      >
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
              <th className="text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                #
              </th>
              <th className="text-right text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                Amount
              </th>
              <th className="text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                Date
              </th>
              {canManageCapital && (
                <th className="text-center text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filteredCapitals.length > 0 ? (
              filteredCapitals.map((capital, i) => {
                const globalIndex = startIndex + i + 1;
                return (
                  <tr
                    key={capital.capitalId || capital.id || i}
                    className="hover:bg-blue-50/50 transition-colors"
                  >
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-xs text-gray-600 font-medium">
                      {globalIndex}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-right font-semibold text-gray-900 text-sm">
                      {formatCurrency(capital.amount)}
                    </td>
                    <td className="py-2 sm:py-3 px-2 sm:px-4 text-[10px] sm:text-sm text-gray-600 hidden md:table-cell">
                      {formatDate(capital.date)}
                    </td>
                    {canManageCapital && (
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <div className="flex gap-1 sm:gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-blue-600 hover:bg-blue-50"
                            onClick={() => {
                              setSelectedCapital(capital);
                              setIsEditModalOpen(true);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faEdit}
                              className="h-3 w-3 sm:h-4 sm:w-4"
                            />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={canManageCapital ? 4 : 3}
                  className="text-center py-6 sm:py-8"
                >
                  <EmptyState
                    title="No capital records found"
                    message={
                      searchTerm || isFilterApplied
                        ? "Try adjusting your search or filters"
                        : canManageCapital
                          ? "Add your first capital record to get started"
                          : "No capital records yet"
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableWrapper>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {canManageCapital && isAddModalOpen && (
        <AddCapital
          onAdd={handleAddCapital}
          onClose={() => setIsAddModalOpen(false)}
          shopId={shopId}
        />
      )}

      {canManageCapital && isEditModalOpen && selectedCapital && (
        <EditCapital
          capital={selectedCapital}
          onUpdate={handleEditCapital}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedCapital(null);
          }}
        />
      )}

      {/* Advanced Filter Modal */}
      {isAdvFilterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Advanced Filter
              </h3>
              <button
                onClick={() => setIsAdvFilterOpen(false)}
                className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-blue-600 h-4 w-4"
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Filter Capital
                  </h4>
                  <p className="text-xs text-gray-600">
                    Select criteria to narrow down results
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Filter by Date
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      From
                    </label>
                    <input
                      type="date"
                      value={tempFilters.startDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      To
                    </label>
                    <input
                      type="date"
                      value={tempFilters.endDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          endDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={clearAdvFilters}
                className="border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none px-6"
              >
                Clear All
              </Button>
              <Button
                onClick={applyAdvFilters}
                className="bg-blue-800 hover:bg-blue-900 text-white px-6 shadow-sm"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapitalTab;
